import React, { useState } from 'react';
import CardWithDivider from '../../../components/Layout/CardWithDivider';
import CardTitle from '../../../components/typography/CardTitle';
import { L } from '../../../lib/i18n';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom-v5-compat';
import { useQueryClient } from 'react-query';
import { FieldValues, UseFormReset } from 'react-hook-form';
import styled from '@emotion/styled';
import * as Dialog from '@radix-ui/react-dialog';
import { Button, Dropzone, onUploadError, onUploadSuccess } from '@ourliving/ourliving-ui';
import { useGetPropertyDocumentTypes } from '../../binders/hooks/propertyHooks/propertyQueries';
import { useGetDocumentSubtypes } from '../../binders/hooks/documentTypeQueries';
import { AddPropertyDocument, DocumentSubtypeSchema, DocumentTypeSchema } from '../../binders/schemas';
import PropertyDocumentForm from '../../binders/components/PropertyDocumentForm';
import useDragEndDnd from '../../../hooks/useDragEndDndNew';
import { getPropertyTemplatesKeys, useGetPropertyTemplateDocuments } from '../hooks/propertyTemplateQueries';
import useUpdatePropertyTemplateDocName from '../../project-templates/hooks/PropertyTemplates/useUpdatePropertyTemplateDocName';
import useUpdatePropertyTemplateDocType from '../../project-templates/hooks/PropertyTemplates/useUpdatePropertyTemplateDocType';
import useUploadPropertyTemplateDocs from '../../project-templates/hooks/PropertyTemplates/useUploadPropertyTemplateDocs';
import useUploadPropertyTemplateDocument from '../../project-templates/hooks/PropertyTemplates/useUploadPropertyTemplateDocument';
import useUpdatePropertyTemplateDocSort from '../../project-templates/hooks/PropertyTemplates/useUpdatePropertyTemplateDocSort';
import { style } from '../../../components/Shared/Style';
import { getCoreRowModel, getSortedRowModel, useReactTable } from '@tanstack/react-table';
import GenericTableDnD from '../../binders/components/GenericTableDnD';
import PropertyDocumentsColumns, {
    PropertyDocumentsTableMeta as TableMeta,
} from '../../binders/components/Properties/PropertyDocumentsColumns';
import useDeletePropertyTemplateDocs from '../../project-templates/hooks/PropertyTemplates/useDeletePropertyTemplateDocs';
import ConfirmationButton from '../../../components/Buttons/ConfirmButton';

const Overlay = styled(Dialog.Overlay)({
    background: 'rgba(0 0 0 / 0.5)',
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    display: 'grid',
    placeItems: 'center',
    overflowY: 'auto',
});

const Content = styled(Dialog.Content)({
    minWidth: 300,
});

function PropertyDocumentsComponent({ propertyId }: { propertyId: string }) {
    const { mutate: updateSortBulk, isLoading: isUpdatingSort } = useUpdatePropertyTemplateDocSort();
    const [open, setOpen] = useState(false);
    const [openUploadMany, setOpenUploadMany] = useState(false);
    const { data: documentTypes } = useGetPropertyDocumentTypes();
    const { data: documentSubtypes } = useGetDocumentSubtypes();
    const { data: documents } = useGetPropertyTemplateDocuments(+propertyId);
    const { mutate: saveDocument } = useUploadPropertyTemplateDocument();
    const { mutate: saveMultipleDocs } = useUploadPropertyTemplateDocs();
    const { mutate: deleteDocuments } = useDeletePropertyTemplateDocs();

    const { mutate: renameDocument } = useUpdatePropertyTemplateDocName();
    const { mutate: updateDocType } = useUpdatePropertyTemplateDocType();

    const queryClient = useQueryClient();
    const handleSaveMultipleDocs = (files: File[]) => {
        saveMultipleDocs(
            { files, propertyId: +propertyId },
            {
                onSuccess: () => {
                    toast.success(L('documents_uploaded'));
                    setOpen(false);
                },
                onError: () => {
                    toast.error(L('pg_unkown'));
                },
                onSettled: () => {
                    queryClient.invalidateQueries(getPropertyTemplatesKeys.documentsByTemplateId(+propertyId));
                },
            },
        );
    };

    const onUpload = (param: onUploadSuccess | onUploadError) => {
        if (param.status === 'error') return;
        handleSaveMultipleDocs([param.file]);
        setOpenUploadMany(false);
    };

    const handleRemoveDocument = (documentId: number) => {
        deleteDocuments(
            { documentIds: [documentId], propertyId: +propertyId },
            {
                onSuccess: () => {
                    toast.success(`${L('document_removed')}`);
                    queryClient.invalidateQueries(getPropertyTemplatesKeys.documentsByTemplateId(+propertyId));
                },
                onError: () => {
                    toast.error(L('pg_unkown'));
                },
            },
        );
    };

    const handleRemoveDocuments = (documentIds: string[]) => {
        deleteDocuments(
            { documentIds: documentIds.map((id) => +id), propertyId: +propertyId },
            {
                onSuccess: (_err, vars) => {
                    if (vars.documentIds.length === 1) toast.success(`${L('document_removed')}`);
                    if (vars.documentIds.length > 1) toast.success(`${L('documents_removed')}`);
                    queryClient.invalidateQueries(getPropertyTemplatesKeys.documentsByTemplateId(+propertyId));
                    tableData.resetRowSelection();
                },
                onError: () => {
                    toast.error(L('pg_unkown'));
                },
            },
        );
    };

    const handleRenameDocument = (documentId: number, name: string) => {
        renameDocument(
            { docId: documentId, name, propertyId: +propertyId },
            {
                onSuccess: () => {
                    toast.success(`${L('renamed_document')}`);
                    queryClient.invalidateQueries(getPropertyTemplatesKeys.documentsByTemplateId(+propertyId));
                },
                onError: () => {
                    toast.error(L('pg_unkown'));
                },
            },
        );
    };

    const handleUpdateDocType = ({
        documentId,
        document_type,
        document_subtype,
    }: {
        documentId: number;
        document_type: number;
        document_subtype: number;
    }) => {
        updateDocType(
            { docId: documentId, document_type, document_subtype, propertyId: +propertyId },
            {
                onSuccess: () => {
                    toast.success(L('updated_document_types'));
                    queryClient.invalidateQueries(getPropertyTemplatesKeys.documentsByTemplateId(+propertyId));
                },
                onError: () => {
                    toast.error(L('pg_unkown'));
                },
            },
        );
    };

    const handleDocSaveMutation = <T extends FieldValues>({
        name,
        document_subtype,
        document_type,
        file,
        reset,
    }: AddPropertyDocument & { reset: UseFormReset<T> }) => {
        const parsedDocumentType = DocumentTypeSchema.parse(document_type);
        const parsedDocumentSubtype = DocumentSubtypeSchema.optional().parse(document_subtype);
        saveDocument(
            {
                file,
                name,
                propertyId: +propertyId,
                documentType: parsedDocumentType,
                documentSubtype: parsedDocumentSubtype,
            },
            {
                onSuccess: () => {
                    toast.success(L('document_uploaded'));
                    reset();
                    setOpen(false);
                },
                onError: () => {
                    toast.error(L('pg_unkown'));
                },
                onSettled: () => {
                    queryClient.invalidateQueries(getPropertyTemplatesKeys.documentsByTemplateId(+propertyId));
                },
            },
        );
    };

    const items = documents?.map((item) => item.id) ?? [];

    const tableData = useReactTable({
        data: documents ?? [],
        columns: PropertyDocumentsColumns,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
        enableColumnResizing: true,
        enableMultiSort: true,
        columnResizeMode: 'onChange',
        getRowId: (originalRow) => originalRow.id.toString(),
        // sorting breaks without sortDescFirst due to some bug
        // https://github.com/TanStack/table/issues/4289
        sortDescFirst: false,
        enableSortingRemoval: true,
        enableRowSelection: true,
        meta: {
            removeDocument: handleRemoveDocument,
            renameDocument: handleRenameDocument,
            updateDocType: handleUpdateDocType,
        } satisfies TableMeta,
    });

    const handleDragEnd = useDragEndDnd({
        queryKey: getPropertyTemplatesKeys.documentsByTemplateId(+propertyId),
        updateSortOrder: updateSortBulk,
        documentableId: +propertyId,
        reverseArray: true,
        isFiltering: tableData.getState().sorting.length > 0,
    });

    return (
        <>
            <Dialog.Root open={!!open} onOpenChange={setOpen}>
                <Dialog.Portal>
                    <Overlay>
                        <Content>
                            <div style={{ width: '400px' }}>
                                {documentTypes && documentSubtypes && (
                                    <PropertyDocumentForm
                                        documentSubtypes={documentSubtypes}
                                        documentTypes={documentTypes}
                                        handleDocSaveMutation={handleDocSaveMutation}
                                    />
                                )}
                            </div>
                        </Content>
                    </Overlay>
                </Dialog.Portal>
            </Dialog.Root>

            <Dialog.Root open={!!openUploadMany} onOpenChange={setOpenUploadMany}>
                <Dialog.Portal>
                    <Overlay>
                        <Content>
                            <div
                                style={{
                                    backgroundColor: style.colors.white,
                                    borderRadius: style.roundCornerSize.small,
                                    padding: '2rem',
                                    width: '400px',
                                }}
                            >
                                <Dropzone onUpload={onUpload} multiple variant="All" />
                            </div>
                        </Content>
                    </Overlay>
                </Dialog.Portal>
            </Dialog.Root>

            <CardWithDivider
                topArea={
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                        <CardTitle>{L('documents')}</CardTitle>
                        <div style={{ display: 'flex', gap: '1rem' }}>
                            <Button onClick={() => setOpenUploadMany(true)}>{L('upload_many')}</Button>
                            <Button onClick={() => setOpen(true)}>{L('new_document')}</Button>
                            <ConfirmationButton
                                disabled={!!(Object.keys(tableData.getState().rowSelection).length < 1)}
                                props={{
                                    onConfirm: () => {
                                        handleRemoveDocuments(Object.keys(tableData.getState().rowSelection));
                                    },
                                    idleVariant: 'secondary',
                                    confirmVariant: 'danger',
                                }}
                            >
                                {L('remove_selected_documents')} :{' '}
                                {Object.keys(tableData.getState().rowSelection).length}
                            </ConfirmationButton>
                        </div>
                    </div>
                }
                mainArea={
                    <>
                        {documents && (
                            <GenericTableDnD
                                items={items}
                                table={tableData}
                                handleDragEnd={handleDragEnd}
                                isUpdatingSort={isUpdatingSort}
                            />
                        )}
                    </>
                }
            />
        </>
    );
}

export default function PropertyDocumentsTemplatePage() {
    const { id: propertyId } = useParams<{ id: string }>();
    return propertyId ? <PropertyDocumentsComponent propertyId={propertyId} /> : null;
}
